import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export interface Badge {
    id: string;
    title: string;
    title_en?: string;
    description: string;
    description_en?: string;
    icon: string;
    color: string;
    categoryId?: number;
    requirement: string;
    image?: string;
}

export const BADGES: Badge[] = [
    {
        id: 'first_quiz',
        title: 'Premier Pas',
        title_en: 'First Step',
        description: 'Compléter votre premier quiz',
        description_en: 'Complete your first quiz',
        icon: 'GraduationCap',
        color: 'blue',
        requirement: 'complete_any_quiz',
        image: '/badges/first_quiz.png'
    },
    {
        id: 'theme_master',
        title: 'Maître du Thème',
        title_en: 'Theme Master',
        description: 'Compléter 100% d\'un thème',
        description_en: 'Complete 100% of a theme',
        icon: 'Trophy',
        color: 'yellow',
        requirement: 'theme_100_percent',
        image: '/badges/theme_master.png'
    },
    {
        id: 'perfect_score',
        title: 'Sans Faute',
        title_en: 'Perfect Score',
        description: 'Obtenir 100% de bonnes réponses sur un quiz',
        description_en: 'Get 100% correct answers on a quiz',
        icon: 'Star',
        color: 'orange',
        requirement: 'quiz_100_percent',
        image: '/badges/perfect_score.png'
    },
    {
        id: 'fast_learner',
        title: 'Apprenti Rapide',
        title_en: 'Fast Learner',
        description: 'Compléter 3 thèmes différents',
        description_en: 'Complete 3 different themes',
        icon: 'Zap',
        color: 'purple',
        requirement: 'complete_3_themes',
        image: '/badges/fast_learner.png'
    },
    {
        id: 'exam_passed',
        title: 'Citoyen Modèle',
        title_en: 'Model Citizen',
        description: 'Réussir un examen blanc (score >= 15/20)',
        description_en: 'Pass a mock exam (score >= 15/20)',
        icon: 'ShieldCheck',
        color: 'green',
        requirement: 'pass_exam',
        image: '/badges/exam_passed.png'
    }
];

export const checkAndAwardBadges = async (userId: string, categoryId: number, stats: { score: number, total: number, progressPercent: number, totalThemesCompleted: number }) => {
    try {
        const { data: user, error: userError } = await supabase.auth.getUser();
        if (userError || !user.user) return [];

        const metadata = user.user.user_metadata || {};
        const existingBadges = metadata.badges || [];
        const newBadges: Badge[] = [];

        // 1. Check for First Quiz Badge
        if (!existingBadges.find((b: any) => b.id === 'first_quiz')) {
            newBadges.push(BADGES.find(b => b.id === 'first_quiz')!);
        }

        // 2. Check for Theme Master Badge
        if (stats.progressPercent >= 100) {
            const badgeId = `theme_master_${categoryId}`;
            if (!existingBadges.find((b: any) => b.id === badgeId)) {
                const baseBadge = BADGES.find(b => b.id === 'theme_master')!;
                newBadges.push({
                    ...baseBadge,
                    id: badgeId,
                    categoryId
                });
            }
        }

        // 3. Check for Perfect Score Badge
        if (stats.score === stats.total && stats.total > 0) {
            if (!existingBadges.find((b: any) => b.id === 'perfect_score')) {
                newBadges.push(BADGES.find(b => b.id === 'perfect_score')!);
            }
        }

        // 4. Check for Fast Learner (3 themes completed)
        if (stats.totalThemesCompleted >= 3) {
            if (!existingBadges.find((b: any) => b.id === 'fast_learner')) {
                newBadges.push(BADGES.find(b => b.id === 'fast_learner')!);
            }
        }

        // 5. Check for Exam Passed Badge (General logic for any successful exam)
        if (categoryId === 0 && stats.score >= 15 && stats.total === 20) {
            if (!existingBadges.find((b: any) => b.id === 'exam_passed')) {
                newBadges.push(BADGES.find(b => b.id === 'exam_passed')!);
            }
        }

        if (newBadges.length > 0) {
            const updatedBadges = [...existingBadges, ...newBadges.map(b => ({
                id: b.id,
                awardedAt: new Date().toISOString(),
                title: b.title,
                title_en: b.title_en,
                description: b.description,
                description_en: b.description_en,
                icon: b.icon,
                color: b.color,
                image: b.image
            }))];

            await supabase.auth.updateUser({
                data: {
                    ...metadata,
                    badges: updatedBadges
                }
            });

            return newBadges;
        }

        return [];
    } catch (error) {
        console.error('Error awarding badge:', error);
        return [];
    }
};
